import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import MainModule from '../../modules/main'
import { encodeSlides, LvcPrimarySliderItem } from '../../modules/sliderUtils'
import {
  AbstractSearchCore,
  MultipleResults,
  ISearchConfig,
  IFacet,
  AbstractSubscriber,
} from '../../modules/search'
import { mapEvent } from '../../modules/utils'
import { config, Font } from '../../constants'
import { Locale } from '../../modules/locale'
import { getSlides } from './functions'

Locale.getInstance().registerKeySet('fr', 'thisWeekSlider', {
  title: 'Cette semaine au Québec',
})

Locale.getInstance().registerKeySet('en', 'thisWeekSlider', {
  title: 'This week in Quebec',
})

export interface LvcThisWeekSlider {
  items: any[]
  total: number
  layout?: 'grid' | 'slider'
  disableTitle: boolean
}
//TODO: add loading
export class ThisWeekSliderSubscriber extends AbstractSubscriber {
  private readonly index: string = 'eventsThisWeek'
  private _core: AbstractSearchCore | undefined
  private searchConfig: ISearchConfig = {}
  private _initialized = false
  private now = new Date()
  private currentDayOfWeek = this.now.getDay()
  private daysUntilEndOfWeek = 6 - this.currentDayOfWeek + 1
  private endOfWeek = new Date(
    this.now.getFullYear(),
    this.now.getMonth(),
    this.now.getDate() + this.daysUntilEndOfWeek,
    23,
    59,
    59
  )
  private nowUnix = Math.floor(this.now.getTime() / 1000)
  private endOfWeekUnix = Math.floor(this.endOfWeek.getTime() / 1000)
  private dateFilters = [this.nowUnix, this.endOfWeekUnix]

  constructor() {
    super('eventsThisWeek')
    let mainModule = MainModule.getInstance()

    mainModule.getSearchManager().subscribe(this)
  }

  async attached(searchCore: AbstractSearchCore): Promise<void> {
    this._core = searchCore
    this._initialized = true
  }

  detaching(): void {}

  async onSearchConfigUpdate(config: ISearchConfig): Promise<void> {}

  async onSearchTextUpdate(text: string): Promise<void> {}

  async onFacetUpdate(facet: IFacet[]): Promise<void> {}

  async onResultUpdate(results: MultipleResults): Promise<void> {}

  async goToSearch() {
    this._core?.applyDateFilters(this.dateFilters, true)
  }

  public async getEventsThisWeek(): Promise<{ items: any; total: number }> {
    this.searchConfig = {
      ...this.searchConfig,
      facets: config.slidersConfigs.thisWeek.facets,
      dateFilters: this.dateFilters,
      hitsPerPage: config.slidersConfigs.thisWeek.hitsPerPage,
    }
    let response: MultipleResults = (await this._core?.executeSearch(
      this.searchConfig
    )) as MultipleResults
    return {
      items: response?.events_search?.items || [],
      total: response?.events_search?.total || 0,
    }
  }
}

//////////////////////////////////////////////////////////////////////////////////////

export class LvcThisWeekSlider extends MinzeElement {
  attrs: Attrs = [
    ['layout', 'grid'],
    ['disable-title', true],
  ]
  reactive: Reactive = ['items', 'total']
  private subscriber: ThisWeekSliderSubscriber = new ThisWeekSliderSubscriber()
  isMobile = window.matchMedia('(max-width: 730px)').matches

  onReady = () => {
    this.loadEvents()
  }
  public loadEvents = async () => {
    let items: LvcPrimarySliderItem[] = []
    const eventsResult = await this.subscriber.getEventsThisWeek()
    this.total = eventsResult.total
    let events = eventsResult.items
    if (events) {
      items = events.map((event: any) => mapEvent(event))
      this.items = items
    } else {
      this.items = []
    }

    this.rerender()

    Minze.cast('minze:lvc-this-week-slider-ready', {
      tagName: 'lvc-this-week-slider',
      title: Locale.getKey('thisWeekSlider.title'),
      total: this.total,
    })
  }
  viewMore = async () => {
    await this.subscriber.goToSearch()
  }

  html = () => {
    if (!this.items || (this.items as any[]).length === 0) {
      return ``
    }
    return /*html*/ ` 
      <lvc-grid-layout data-component-location="activity_pack" data="${encodeSlides(this.items.filter(item => item).slice(0, 14))}"></lvc-grid-layout>
      <div class="view-more-layout">
        <div id="view-more">
          <div class="view-more-text">${Locale.getKey('lvc-activity-pack.viewMore', { title: Locale.getKey('thisWeekSlider.title') })}</div>
          <div class="view-more-icon-wrapper">
            <lvc-arrow-right-icon></lvc-arrow-right-icon>
          </div>
        </div>
      </div>
      <lvc-slider-title text="${Locale.getKey('thisWeekSlider.title')}" counter="${this.total}"></lvc-slider-title>
      <lvc-primary-slider
        data-component-location="activity_pack"
        list-name="${Locale.getKey('thisWeekSlider.title')}"
        items-per-slide="3"
        fade-effect="true"
        data-slides="${encodeSlides(getSlides(this.items, this.total, 'lvc:this-week-slider-see-more'))}"
        style="margin-bottom: 1rem;"
        ></lvc-primary-slider> 
  `
  }
  css = () => `
      :host {
        ${!this.items || (this.items as any[]).length === 0 ? 'display: none;' : ''}
      }
     
      lvc-grid-layout {
        display: ${!this.layout || this.layout === 'grid' ? 'grid' : 'none'};
      }
      lvc-primary-slider {
        display: ${this.layout === 'slider' ? 'block' : 'none'};
      }
      .view-more-layout {
        display: ${!this.layout || this.layout === 'grid' ? 'flex' : 'none'};
        margin: auto;
        justify-content: center;
        margin-top: 60px;
      }
      #view-more {
        font-size: 1.5rem;
        font-weight: 500;
        color: #1E007D;
        text-transform: uppercase;
        display: flex;
        align-items: center;
        gap: 20px;
        cursor: pointer;
      }
      .view-more-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        background-color: #ffffff;
        border-radius: 100%;
      }
      lvc-arrow-right-icon {
        width: 35px;
        height: 35px;
        transform: rotate(-25deg);
      }
      .view-more-text {
        border-bottom: 1px solid #1E007D;
      }
      .title-container {
        display: ${!this.layout || this.layout === 'grid' ? 'none' : 'flex'};
        align-items: center;
        column-gap: 10px;
        cursor: pointer;
      }
      .counter {
        display: inline-flex;
        height: 34px;
        min-width: 34px;
        padding: 7px;
        justify-content: center;
        align-items: center;
        border-radius: 40px;
        background: #1E007D;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
        color: #FF4600;
        position: relative;
        top: 2px;
      }
      @media only screen and (max-width: 991px) {
        lvc-grid-layout {
          display: none;
        }
        lvc-primary-slider {
          display: block;
        }
        .title-container {
          display: flex;
        }
        .view-more-layout {
          display: none;
        }
      }
      /* @media screen and (min-width: 600px) {
        lvc-primary-slider {
          height: 450px;
        }
      } */
  `
  eventListeners: EventListeners = [
    [window, 'lvc:this-week-slider-see-more', this.viewMore],
    ['.title-container', 'click', this.viewMore],
    ['#view-more', 'click', this.viewMore],
  ]
}
